import dayjs, { Dayjs } from 'dayjs';
import {
  DATE_OF_BIRTH_MESSAGES,
  MAXIMUM_AGE_YEARS,
  MINIMUM_AGE_YEARS,
} from '../constants/dateOfBirth';

export function formatDate(
  date: Date,
  includeYear = false,
  shortMonth = false
): string {
  let options: Intl.DateTimeFormatOptions;
  if (!includeYear) {
    options = {
      month: shortMonth ? 'short' : 'long',
      day: 'numeric',
      timeZone: 'UTC',
    };
  } else {
    options = {
      month: shortMonth ? 'short' : 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    };
  }

  const formatter = new Intl.DateTimeFormat('en-US', options);
  const formattedDate = formatter.format(date);

  const day = date.getUTCDate();
  const suffix = getOrdinalSuffix(day);

  return formattedDate.replace(/\d+/, `${day}${suffix}`);
}

export const getMonthName = (monthNumber: number) => {
  const date = new Date();
  date.setMonth(monthNumber - 1); // JavaScript months are 0-indexed
  return date.toLocaleString('en-US', { month: 'long' });
};

export function getOrdinalSuffix(day: number): string {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

export const getDayWithSuffix = (date: Date) => {
  const day = date.getDate();
  if (day > 3 && day < 21) return day + 'th';
  switch (day % 10) {
    case 1:
      return day + 'st';
    case 2:
      return day + 'nd';
    case 3:
      return day + 'rd';
    default:
      return day + 'th';
  }
};

export function formatDateWithTime(
  date: Date,
  includeYear = false,
  shortMonth = false,
  timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone
): string {
  let options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    month: shortMonth ? 'short' : 'long',
    day: 'numeric',
    timeZone: 'UTC',
    ...(includeYear && { year: 'numeric' }),
  };

  const formatter = new Intl.DateTimeFormat('en-US', { ...options, timeZone });
  const formattedDate = formatter.format(date);

  // Add the ordinal suffix to the day
  const day = date.getDate();
  const suffix = getOrdinalSuffix(day);

  return formattedDate.replace(/\d+/, `${day}${suffix}`);
}

export const getCurrentMonthAndDayName = (short: boolean = false): string => {
  const date = new Date();
  const monthName = date.toLocaleString('en-US', {
    month: short ? 'short' : 'long',
  });
  const dayWithSuffix = getDayWithSuffix(date);
  return `${monthName} ${dayWithSuffix}`;
};

/** Progressive "MM/DD/YYYY" (adds slashes after 2 & 4 digits). Accepts partials. */
export function formatUSDatePretty(input: string): string {
  const d = (input || '').replace(/\D/g, '').slice(0, 8); // MMDDYYYY max
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

/** On blur: left-pad month/day, cap to 10 chars if year present. */
export function normalizeUSDatePretty(input: string): string {
  const digits = (input || '').replace(/\D/g, '');
  if (!digits) return '';
  const mm = digits.slice(0, 2).padStart(2, '0');
  const dd = digits.slice(2, 4).padStart(2, '0');
  const yyyy = digits.slice(4, 8);
  return [mm, dd, yyyy].filter(Boolean).join('/').slice(0, 10);
}

/** Strict calendar validation (handles leap years). */
export function isValidUSDate(pretty: string): boolean {
  const m = pretty.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return false;
  const mm = +m[1],
    dd = +m[2],
    yyyy = +m[3];
  if (mm < 1 || mm > 12) return false;
  if (yyyy < 1900 || yyyy > 2100) return false;
  const dim = new Date(yyyy, mm, 0).getDate();
  return dd >= 1 && dd <= dim;
}

/** Pretty -> ISO */
export function parseUSDateToISO(pretty: string): string | null {
  if (!isValidUSDate(pretty)) return null;
  const [mm, dd, yyyy] = pretty.split('/');
  return `${yyyy}-${mm}-${dd}`;
}

/** ISO -> Pretty */
export function isoToUSPretty(iso?: string | null): string {
  if (!iso) return '';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
}

/**
 * Validates a date of birth. Returns an error message, or null when the value is
 * acceptable. Empty is acceptable here — the field's `required` rule owns that case.
 *
 * Takes the ISO `YYYY-MM-DD` string the signup form holds.
 *
 * Note this is deliberately NOT wired into the picker as `disabledDate`: rc-picker
 * silently refuses to commit a disabled date that was typed, so the field would
 * clear itself with no explanation. Validating the committed value instead means
 * typing and picking both surface the same message.
 */
export function getDateOfBirthError(value?: string | null): string | null {
  if (!value) return null;

  const dob = dayjs(value);
  if (!dob.isValid()) return DATE_OF_BIRTH_MESSAGES.invalid;

  const today = dayjs().startOf('day');
  if (dob.isAfter(today, 'day')) return DATE_OF_BIRTH_MESSAGES.future;

  const age = today.diff(dob, 'year');
  if (age < MINIMUM_AGE_YEARS) return DATE_OF_BIRTH_MESSAGES.underage;
  if (age > MAXIMUM_AGE_YEARS) return DATE_OF_BIRTH_MESSAGES.implausible;

  return null;
}

/**
 * Month the date-of-birth calendar should open on. antd opens on the current
 * month, which is 18+ years of paging away from any valid answer, so an empty
 * field starts at the newest birth date that clears the minimum.
 *
 * Returns undefined once the field has a value: rc-picker re-seeds the panel from
 * `defaultPickerValue` every time it opens, which would otherwise drag an existing
 * date's panel off its own year.
 */
export const dateOfBirthPickerMonth = (
  currentValue?: string | null
): Dayjs | undefined =>
  currentValue
    ? undefined
    : dayjs().startOf('day').subtract(MINIMUM_AGE_YEARS, 'year');
